import { Component, DestroyRef, OnInit, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentChatService } from '../../agent/agent-chat.service';
import { AuthService } from '../../services/auth.service';
import { OrganisationFeatures, OrganisationService } from '../../services/organisation.service';
import { canSeeCreditAnalytics } from '../credit-analytics/credit-analytics.access';

/**
 * Einstellungen. Reiter wie auf „Organisation" (nb-tabset, Stand in `?tab=`):
 *  - `account`: Profil, Passwort, zweiter Faktor — für jeden Nutzer.
 *  - `ai`: KI & Limits — Monatskontingent in Credits je interner Nutzer und
 *    Bereich. Nur Kunden-Admins; bis zur Freigabe der Credit-Oberfläche nur
 *    super_admin (dieselbe Entscheidung wie „Credit-Verbrauch",
 *    `credit-analytics.access.ts`). Das Backend erzwingt dasselbe.
 *  - `agent-access`: eigenen KI-Agenten per MCP verbinden (persönliche
 *    Tokens) — jeder Nutzer, sobald der Portal-Assistent an ist.
 *  - `connections`: Figma für den Kurs-Editor (eigenes Konto, admin auch das
 *    der Organisation) — wer den Creator hat (gleiche Prüfung wie das Menü).
 */
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  readonly tabIds = ['account', 'ai', 'agent-access', 'connections'] as const;
  readonly agentChat = inject(AgentChatService);
  private readonly destroyRef = inject(DestroyRef);
  activeTab: string = 'account';
  readonly showAiLimits: boolean;

  /** `?tab=agent-access` before the agent status has loaded — the tab appears later. */
  private wantsAgentAccess = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organisationService: OrganisationService,
    private authService: AuthService,
  ) {
    effect(() => {
      if (this.agentChat.enabled() && this.wantsAgentAccess) {
        this.wantsAgentAccess = false;
        queueMicrotask(() => this.onTabChange('agent-access'));
      }
    });
    const role = authService.accessTokenPayload$.value?.role;
    // Kunden-Admin oder super_admin — und die Credit-Freigabe entscheidet, ob
    // Kunden-Admins schon dazugehoeren.
    this.showAiLimits = canSeeCreditAnalytics(role) && (role === 'admin' || role === 'super_admin');
  }

  /** The organisation loads after a reload — a getter, so the tab appears with it. */
  get showConnections(): boolean {
    return (
      this.organisationService.hasFeature(OrganisationFeatures.CONTENT_GENERATOR) &&
      this.authService.hasFeatureAccess('CONTENT_GENERATOR')
    );
  }

  ngOnInit(): void {
    const requested = this.route.snapshot.queryParamMap.get('tab');
    if (requested === 'ai' && this.showAiLimits) {
      this.activeTab = 'ai';
    } else if (requested === 'connections') {
      // The course editor links here. After a reload the tab appears only once
      // the organisation has loaded — the tabset has picked „Konto" by then.
      this.organisationService.organisation$
        .pipe(
          filter(() => this.showConnections),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => queueMicrotask(() => this.onTabChange('connections')));
    } else if (requested === 'agent-access') {
      if (this.agentChat.enabled()) this.activeTab = 'agent-access';
      else this.wantsAgentAccess = true;
    }
  }

  onTabChange(tabId: string): void {
    if (!this.tabIds.includes(tabId as any) || tabId === this.activeTab) return;
    this.activeTab = tabId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
